import type { Client, Contact, Program } from '@efdigital/ll-addon-data';
import {
  EditRecord,
  initialState,
} from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { type EditRecordSignature } from '@efdigital/ll-admin-v2/components/edit-record-base.gts';
import {
  setAsyncBelongsTo,
  setAsyncHasMany,
} from '@efdigital/ll-admin-v2/data/async-setter';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

import ClientPicker from '../pickers/client-picker.gts';
import { Validation } from '../validation.gts';

export class EditProgram extends Component<EditRecordSignature<Program>> {
  @service declare store: Store;

  handleClientSelect = (client: Client | null) => {
    setAsyncBelongsTo(this.args.item, 'client', client);
  };

  handleContactSelect = async (contact: Contact) => {
    const contacts = await this.args.item.contacts;
    if (!contacts.find((item) => item.id === contact.id)) {
      setAsyncHasMany(this.args.item, 'contacts', [...contacts, contact]);
    }
  };

  removeContact = async (contact: Contact) => {
    const contacts = await this.args.item.contacts;
    setAsyncHasMany(
      this.args.item,
      'contacts',
      contacts.filter((c) => c.id !== contact.id)
    );
  };

  setTitle = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.title = target.value;
  };

  validate = (item: Program) => {
    if (!item.client?.id) {
      return Validation.fromFailures([
        {
          message: 'Client is required',
          attribute: 'client',
        },
      ]);
    }

    return Validation.success();
  };

  <template>
    <EditRecord
      @editLabel="Edit Program"
      @item={{@item}}
      @context={{@context}}
      @buttonSize="btn-md"
      @initialState={{initialState @item}}
      @validate={{this.validate}}
      @maxDepth={{1}}
      ...attributes
    >
      <:form as |uniqueId isReadOnly|>
        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Details</legend>

          <div class="mb-3">
            <label for="{{uniqueId}}-title" class="form-label required">
              Program Name
            </label>
            <input
              name="title"
              type="text"
              class="form-control"
              id="{{uniqueId}}-title"
              value={{@item.title}}
              required
              placeholder="Program Name"
              {{on "change" this.setTitle}}
            />
          </div>

          <div class="mb-3">
            <Await @promise={{@item.client}}>
              <:success as |client|>
                <ClientPicker
                  @client={{client}}
                  @onSelect={{this.handleClientSelect}}
                  @readonly={{isReadOnly}}
                  @allowPublic={{false}}
                  @label="Client"
                />
              </:success>
            </Await>
          </div>
        </fieldset>
      </:form>
    </EditRecord>
  </template>
}
